import { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { ChartFragment } from 'models/api-response';
import { FormFieldInputType, ValidationActionType } from 'models/enum';
import { ChartActionsComponentProps, ChartComponentProps, ChartMetaFormField, ValidationRule } from 'models/ui';
import { chartHelper } from 'helpers';
import { chartService } from 'services';
import { studentSelectors } from 'redux/ducks/student';
import { withChartLogic } from 'components/common';
import { Title } from 'components/features/chart/special-charts/shared/constants';
import withSavedPatientChartsPage from 'components/features/shared/withSavedPatientChartsPage';
import {
  CONSENT_PARAGRAPHS,
  CONSENT_SENTENCE,
  DecisionValue,
  FormField,
  FormFieldLabel,
  NON_REQUIRED_FIELDS,
  REQUIRED_SIGNATURE_FIELDS,
  SELECT_DAY_LABEL,
  SectionTitle
} from './constants';
import ConsentForBloodTransfusionView, { ConsentForBloodTransfusionViewProps } from './ConsentForBloodTransfusionView';

const buildProviderNurseRecord = (isAuthor: boolean, authorName: string, student: { firstName?: string; lastName?: string } | undefined) => {
  const resolvedName = isAuthor ? (authorName || '').trim() : `${student?.firstName ?? ''} ${student?.lastName ?? ''}`.trim();
  if (!resolvedName) return null;
  return chartService.buildPlainRecord({
    formField: FormField.PROVIDER_NURSE,
    title: FormFieldLabel.PROVIDER_NURSE,
    content: resolvedName,
    value: resolvedName,
    contentId: ''
  });
};

interface ConsentForBloodTransfusionProps extends ChartComponentProps {
  navigateToSavedPatientCharting: VoidFunction;
  authorName?: string;
}

class ConsentForBloodTransfusion extends Component<ConsentForBloodTransfusionProps> {
  static displayName = 'ConsentForBloodTransfusion';

  componentDidMount() {
    this.props.initState(this.buildFormFields());
  }

  getDecisionOptions = (selectedValue: string) => {
    const normalizedValue = String(selectedValue || '')
      .trim()
      .toLowerCase();
    return [
      { id: DecisionValue.CONSENT, label: 'Consent', value: DecisionValue.CONSENT, selected: normalizedValue === DecisionValue.CONSENT },
      { id: DecisionValue.REFUSE, label: 'Refuse', value: DecisionValue.REFUSE, selected: normalizedValue === DecisionValue.REFUSE }
    ] as any;
  };

  createField = ({ name, type, label, value = '' }: { name: FormField; type: FormFieldInputType; label?: string; value?: string }): ChartMetaFormField => {
    return chartHelper.createFormField({ name, type, label, value });
  };

  buildFormFields = (chartData: Partial<Record<FormField, string>> = {}): Map<string, ChartMetaFormField> => {
    const dataMap = new Map<string, ChartMetaFormField>();

    dataMap.set(
      FormField.PATIENT_LEGAL_GUARDIAN,
      this.createField({
        name: FormField.PATIENT_LEGAL_GUARDIAN,
        type: FormFieldInputType.TEXT_BOX,
        label: FormFieldLabel.PATIENT_LEGAL_GUARDIAN,
        value: chartData[FormField.PATIENT_LEGAL_GUARDIAN] || ''
      })
    );
    dataMap.set(
      FormField.PATIENT_NAME,
      this.createField({
        name: FormField.PATIENT_NAME,
        type: FormFieldInputType.TEXT_BOX,
        label: FormFieldLabel.PATIENT_NAME,
        value: chartData[FormField.PATIENT_NAME] || ''
      })
    );
    const decisionField = this.createField({
      name: FormField.DECISION,
      type: FormFieldInputType.RADIO_CHOICE,
      label: FormFieldLabel.DECISION,
      value: chartData[FormField.DECISION] || ''
    });
    decisionField.chartContent = this.getDecisionOptions(chartData[FormField.DECISION] || '');
    dataMap.set(FormField.DECISION, decisionField);

    dataMap.set(
      FormField.PATIENT_GUARDIAN_SIGNATURE,
      this.createField({
        name: FormField.PATIENT_GUARDIAN_SIGNATURE,
        type: FormFieldInputType.TEXT_BOX,
        label: FormFieldLabel.PATIENT_GUARDIAN_SIGNATURE,
        value: chartData[FormField.PATIENT_GUARDIAN_SIGNATURE] || ''
      })
    );
    dataMap.set(
      FormField.PATIENT_GUARDIAN_DAY,
      this.createField({
        name: FormField.PATIENT_GUARDIAN_DAY,
        type: FormFieldInputType.DROPDOWN,
        label: SELECT_DAY_LABEL,
        value: chartData[FormField.PATIENT_GUARDIAN_DAY] || ''
      })
    );
    dataMap.set(
      FormField.PROVIDER_DESIGNEE_SIGNATURE,
      this.createField({
        name: FormField.PROVIDER_DESIGNEE_SIGNATURE,
        type: FormFieldInputType.TEXT_BOX,
        label: FormFieldLabel.PROVIDER_DESIGNEE_SIGNATURE,
        value: chartData[FormField.PROVIDER_DESIGNEE_SIGNATURE] || ''
      })
    );
    dataMap.set(
      FormField.PROVIDER_DESIGNEE_DAY,
      this.createField({
        name: FormField.PROVIDER_DESIGNEE_DAY,
        type: FormFieldInputType.DROPDOWN,
        label: SELECT_DAY_LABEL,
        value: chartData[FormField.PROVIDER_DESIGNEE_DAY] || ''
      })
    );
    dataMap.set(
      FormField.WITNESS_SIGNATURE,
      this.createField({
        name: FormField.WITNESS_SIGNATURE,
        type: FormFieldInputType.TEXT_BOX,
        label: FormFieldLabel.WITNESS_SIGNATURE,
        value: chartData[FormField.WITNESS_SIGNATURE] || ''
      })
    );
    dataMap.set(
      FormField.WITNESS_DAY,
      this.createField({ name: FormField.WITNESS_DAY, type: FormFieldInputType.DROPDOWN, label: SELECT_DAY_LABEL, value: chartData[FormField.WITNESS_DAY] || '' })
    );

    return dataMap;
  };

  getDecisionValue = (): string => {
    const decisionField = this.props.formFieldMap.get(FormField.DECISION);
    return chartService.buildRecordFromField(decisionField)?.value || '';
  };

  shouldShowSignatureFields = (): boolean => {
    const decisionValue = this.getDecisionValue();
    return decisionValue === DecisionValue.CONSENT || decisionValue === DecisionValue.REFUSE;
  };

  getConditionalRequiredFieldNames = (): string[] => {
    return this.shouldShowSignatureFields() ? REQUIRED_SIGNATURE_FIELDS : [];
  };

  getRequiredFieldNames = (): string[] => {
    const requiredRules: ValidationRule[] = (this.props.chartMetadata?.chartFieldValidationSet || []).filter((rule) => rule.validationActionType === ValidationActionType.REQUIRED);

    const metadataRequiredFieldNames = Array.from(new Set(requiredRules.map((rule) => rule.formFieldId).filter(Boolean))).filter(
      (fieldName) => !NON_REQUIRED_FIELDS.includes(fieldName)
    );

    return Array.from(new Set([...metadataRequiredFieldNames, ...this.getConditionalRequiredFieldNames()]));
  };

  isFieldFilled = (fieldName: string): boolean => {
    const formField = this.props.formFieldMap.get(fieldName);
    const record = chartService.buildRecordFromField(formField);
    const value = record?.value ?? formField?.value ?? '';
    return String(value).trim().length > 0;
  };

  hasBlockingErrors = (): boolean => {
    return this.getRequiredFieldNames().some((fieldName) => {
      const field = this.props.formFieldMap.get(fieldName);
      return Boolean(field?.errors?.length);
    });
  };

  hasValidUnsavedChanges = (): boolean => {
    if (!this.props.hasUnsavedChanges) {
      return false;
    }

    if (this.hasBlockingErrors()) {
      return false;
    }

    const requiredFieldNames = this.getRequiredFieldNames();
    return requiredFieldNames.every((fieldName) => this.isFieldFilled(fieldName));
  };

  handleCancelClick = () => this.props.handleDiscardClick(undefined, this.buildFormFields());

  getFieldValue = (fieldName: FormField): string => {
    const formField = this.props.formFieldMap.get(fieldName);
    return String(chartService.buildRecordFromField(formField)?.value ?? '').trim();
  };

  getDecisionLabel = (): string => {
    const decisionValue = this.getDecisionValue();
    if (decisionValue === DecisionValue.CONSENT) return 'Consent';
    if (decisionValue === DecisionValue.REFUSE) return 'Refuse';
    return '';
  };

  formatDay = (value: string): string => (value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : '');

  formatSignatory = (signature: string, day: string): string => [signature, this.formatDay(day)].filter(Boolean).join(' | ');

  buildFragment = (): ChartFragment => {
    const { buildPlainRecord, buildPatientRecord } = chartService;

    const guardianValue = this.getFieldValue(FormField.PATIENT_LEGAL_GUARDIAN);
    const patientNameValue = this.getFieldValue(FormField.PATIENT_NAME);
    const decisionLabel = this.getDecisionLabel();
    const legalText = [CONSENT_SENTENCE.FOLLOW_UP, ...CONSENT_PARAGRAPHS].join('\n\n');

    const record = {
      chartTitle: Title.SPECIAL_CHARTS,
      fragmentTitle: SectionTitle.CONSENT_FOR_BLOOD_TRANSFUSION,
      records: [
        {
          sectionTitle: SectionTitle.GENERAL_INFORMATION,
          records: [buildProviderNurseRecord(this.props.isAuthor, this.props.authorName, this.props.student)].filter(Boolean)
        },
        {
          sectionTitle: 'Consent',
          contentSectionTitle: 'Consenter',
          records: [
            buildPlainRecord({ formField: 'consentLeadIn', title: CONSENT_SENTENCE.LEAD_IN, content: guardianValue }),
            buildPlainRecord({ formField: FormField.PATIENT_LEGAL_GUARDIAN, title: CONSENT_SENTENCE.AUTHORIZE, content: patientNameValue }),
            buildPlainRecord({ formField: 'consentLegalText', title: legalText, content: '' }),
            buildPlainRecord({ formField: 'consentDiscussion', title: CONSENT_SENTENCE.DISCUSSION, content: decisionLabel }),
            buildPlainRecord({ formField: FormField.DECISION, title: CONSENT_SENTENCE.USE_OF_BLOOD, content: decisionLabel })
          ]
        },
        {
          sectionTitle: 'Signatory',
          contentSectionTitle: 'Signature',
          records: [
            buildPlainRecord({
              formField: FormField.PATIENT_GUARDIAN_SIGNATURE,
              title: FormFieldLabel.PATIENT_GUARDIAN_SIGNATURE,
              content: this.formatSignatory(this.getFieldValue(FormField.PATIENT_GUARDIAN_SIGNATURE), this.getFieldValue(FormField.PATIENT_GUARDIAN_DAY))
            }),
            buildPlainRecord({
              formField: FormField.PROVIDER_DESIGNEE_SIGNATURE,
              title: FormFieldLabel.PROVIDER_DESIGNEE_SIGNATURE,
              content: this.formatSignatory(this.getFieldValue(FormField.PROVIDER_DESIGNEE_SIGNATURE), this.getFieldValue(FormField.PROVIDER_DESIGNEE_DAY))
            }),
            buildPlainRecord({
              formField: FormField.WITNESS_SIGNATURE,
              title: FormFieldLabel.WITNESS_SIGNATURE,
              content: this.formatSignatory(this.getFieldValue(FormField.WITNESS_SIGNATURE), this.getFieldValue(FormField.WITNESS_DAY))
            })
          ]
        }
      ]
    };

    const basicInfo = chartService.createBaseFragment({ chartingTime: this.props.chartingTime });
    return {
      ...basicInfo,
      chartData: record
    } as ChartFragment;
  };

  handleSaveClick = () => {
    this.props.saveChart([this.buildFragment()], { defaultFormFields: this.buildFormFields(), afterSave: this.props.navigateToSavedPatientCharting });
  };

  render() {
    const chartActionsProps: ChartActionsComponentProps = {
      enableDisplayRecordsButton: this.props.enableDisplayRecordsButton,
      enableSaveButton: this.hasValidUnsavedChanges(),
      saveButtonHasIcon: true,
      saveButtonText: 'Save & Complete',
      cancelButtonText: 'Discard Changes',
      onCancelClick: this.handleCancelClick,
      onDisplayRecordsClick: this.props.displayAuthoringData,
      onSaveClick: this.handleSaveClick
    };

    const viewProps: ConsentForBloodTransfusionViewProps = {
      chartActionsProps,
      formFieldMap: this.props.formFieldMap,
      formSubmittedCount: this.props.formSubmittedCount,
      showSignatureFields: this.shouldShowSignatureFields()
    };

    return <ConsentForBloodTransfusionView {...viewProps} />;
  }
}

export { ConsentForBloodTransfusion as BaseConsentForBloodTransfusion };

const mapStateToProps = (state) => ({
  authorName: studentSelectors.getAuthorName(state)
});

export default compose(connect(mapStateToProps), withSavedPatientChartsPage, withChartLogic)(ConsentForBloodTransfusion);
